import { useState, useEffect } from 'react';
import { usePlanPermissions, type PlanPermissions } from '../../hooks/usePlanPermissions';
import { UpgradeModal } from './UpgradeModal';
import { trackEvent } from '../../lib/analytics';
import { useConversionContext } from '../../contexts/ConversionTriggersContext';

type PermissionKey = keyof Pick<
  PlanPermissions,
  | 'canAccessReports'
  | 'canExportCsv'
  | 'canExportPdf'
  | 'canAccessAdvancedInsights'
  | 'canUseMultipleCurrencies'
  | 'canAccessLoansModule'
  | 'canAccessRecurringAdvanced'
  | 'canAccessPrioritySupport'
  | 'canUsePayeesModule'
>;

interface PlanGateProps {
  feature: PermissionKey;
  children: React.ReactNode;
  mode?: 'page' | 'inline';
  title?: string;
  description?: string;
  label?: string;
}

// ── Inline locked badge ─────────────────────────────── */
const InlineLocked = ({ label, onUpgrade }: { label?: string; onUpgrade: () => void }) => (
  <button
    onClick={onUpgrade}
    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-[12px] font-600 cursor-pointer hover:bg-amber-100 transition-colors whitespace-nowrap"
    title="Recurso disponível em planos pagos"
  >
    <i className="ri-lock-line text-[13px]" />
    {label ?? 'Bloqueado'}
    <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full ml-1">PRO</span>
  </button>
);

// ── Premium feature preview tiles (visual teaser) ─────
const FEATURE_PREVIEW_MAP: Record<PermissionKey, Array<{ icon: string; label: string; sub: string }>> = {
  canAccessReports: [
    { icon: 'ri-bar-chart-2-line', label: 'Receitas vs Despesas', sub: 'Gráfico de evolução mensal' },
    { icon: 'ri-pie-chart-line', label: 'Por Categoria', sub: 'Distribuição de gastos detalhada' },
    { icon: 'ri-exchange-funds-line', label: 'Fluxo de Caixa', sub: 'Projeção de 30/60/90 dias' },
    { icon: 'ri-table-line', label: 'Tabela Analítica', sub: 'Todos os lançamentos filtráveis' },
    { icon: 'ri-download-line', label: 'Exportar CSV/PDF', sub: 'Leve seus dados para onde quiser' },
    { icon: 'ri-lightbulb-flash-line', label: 'Insights Automáticos', sub: 'Análises geradas com IA' },
  ],
  canExportCsv: [
    { icon: 'ri-file-text-line', label: 'CSV completo', sub: 'Todos os campos exportados' },
    { icon: 'ri-filter-line', label: 'Filtros aplicados', sub: 'Exporta exatamente o que você vê' },
    { icon: 'ri-google-line', label: 'Google Sheets', sub: 'Compatível com planilhas' },
  ],
  canExportPdf: [
    { icon: 'ri-file-pdf-line', label: 'Relatório em PDF', sub: 'Layout profissional e formatado' },
    { icon: 'ri-printer-line', label: 'Pronto para imprimir', sub: 'Alta qualidade de impressão' },
    { icon: 'ri-share-line', label: 'Fácil de compartilhar', sub: 'Envie por e-mail ou WhatsApp' },
  ],
  canAccessAdvancedInsights: [
    { icon: 'ri-lightbulb-flash-line', label: 'Insights com IA', sub: 'Análise automática dos seus dados' },
    { icon: 'ri-arrow-up-down-line', label: 'Comparativos', sub: 'Mês a mês, categoria a categoria' },
    { icon: 'ri-alarm-warning-line', label: 'Alertas inteligentes', sub: 'Gastos fora do padrão' },
  ],
  canUseMultipleCurrencies: [
    { icon: 'ri-global-line', label: 'Multi-moedas', sub: 'USD, EUR, BTC e mais' },
    { icon: 'ri-exchange-dollar-line', label: 'Conversão automática', sub: 'Cotação em tempo real' },
    { icon: 'ri-bank-line', label: 'Conta em moeda estrangeira', sub: 'Saldo em moeda nativa' },
  ],
  canAccessLoansModule: [
    { icon: 'ri-bank-card-2-line', label: 'Controle de empréstimos', sub: 'Parcelas e saldo devedor' },
    { icon: 'ri-percent-line', label: 'Cálculo de juros', sub: 'Amortização automática' },
    { icon: 'ri-calendar-check-line', label: 'Vencimentos', sub: 'Alertas de parcela chegando' },
  ],
  canAccessRecurringAdvanced: [
    { icon: 'ri-repeat-line', label: 'Recorrência avançada', sub: 'Configure dia, duração e fim' },
    { icon: 'ri-calendar-line', label: 'Parcelamento', sub: 'Controle de parcelas automático' },
    { icon: 'ri-notification-3-line', label: 'Lembretes', sub: 'Aviso antes do vencimento' },
  ],
  canAccessPrioritySupport: [
    { icon: 'ri-customer-service-line', label: 'Suporte prioritário', sub: 'Resposta em até 24h' },
    { icon: 'ri-chat-3-line', label: 'Chat dedicado', sub: 'Atendimento especializado' },
    { icon: 'ri-shield-check-line', label: 'SLA garantido', sub: 'Respostas no prazo' },
  ],
  canUsePayeesModule: [
    { icon: 'ri-user-3-line', label: 'Cadastro de favorecidos', sub: 'Pessoa física e jurídica' },
    { icon: 'ri-history-line', label: 'Histórico por favorecido', sub: 'Todos os lançamentos vinculados' },
    { icon: 'ri-notification-badge-line', label: 'Alertas inteligentes', sub: 'Gasto acima da média' },
  ],
};

// ── Page locked state ───────────────────────────────── */
const PageLocked = ({
  feature,
  title,
  description,
  planName,
  onUpgrade,
}: {
  feature: PermissionKey;
  title: string;
  description: string;
  planName: string;
  onUpgrade: () => void;
}) => {
  const previews = FEATURE_PREVIEW_MAP[feature] ?? [];

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4 py-8">
      <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-5">
        <i className="ri-vip-crown-fill text-3xl text-emerald-500" />
      </div>

      <h2 className="text-xl font-700 text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-500 text-[14px] max-w-md leading-relaxed mb-1">{description}</p>
      <p className="text-[12px] text-gray-400 mb-7">
        Seu plano atual (<strong>{planName}</strong>) não inclui este recurso.
      </p>

      {/* CTA buttons */}
      <div className="flex flex-col items-center gap-2 w-full max-w-xs mb-10">
        <button
          onClick={onUpgrade}
          className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-700 rounded-xl cursor-pointer transition-colors text-[15px] flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <i className="ri-vip-crown-line" />
          Ver planos e fazer upgrade
        </button>
        <p className="text-[11px] text-gray-400">Acesso liberado instantaneamente após o pagamento</p>
      </div>

      {/* Feature preview grid — visual teaser of what they'd get */}
      {previews.length > 0 && (
        <div className="w-full max-w-xl">
          <p className="text-[11px] font-700 uppercase tracking-wide text-gray-400 mb-3">
            O que você desbloqueia
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {previews.map((item) => (
              <div
                key={item.label}
                className="bg-white border border-gray-100 rounded-xl p-4 flex flex-col items-start gap-2 text-left"
              >
                <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <i className={`${item.icon} text-emerald-500 text-[17px]`} />
                </div>
                <div>
                  <p className="text-[12px] font-700 text-gray-800">{item.label}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main PlanGate component ─────────────────────────── */
export const PlanGate = ({
  feature,
  children,
  mode = 'page',
  title,
  description,
  label,
}: PlanGateProps) => {
  const permissions = usePlanPermissions();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { trackFeatureBlock } = useConversionContext();

  // Track feature block on first render when access is denied
  useEffect(() => {
    if (!permissions.loading && !(permissions[feature] as boolean)) {
      trackFeatureBlock(feature);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.loading]);

  if (permissions.loading) {
    if (mode === 'page') {
      return (
        <div className="flex items-center justify-center min-h-[40vh]">
          <i className="ri-loader-4-line text-2xl text-emerald-500 animate-spin" />
        </div>
      );
    }
    return <>{children}</>;
  }

  const isAllowed = permissions[feature] as boolean;

  if (isAllowed) return <>{children}</>;

  const defaultTitles: Record<PermissionKey, string> = {
    canAccessReports: 'Relatórios & Insights',
    canExportCsv: 'Exportar para CSV',
    canExportPdf: 'Exportar para PDF',
    canAccessAdvancedInsights: 'Insights Avançados',
    canUseMultipleCurrencies: 'Multi-moedas',
    canAccessLoansModule: 'Módulo de Empréstimos',
    canAccessRecurringAdvanced: 'Recorrência Avançada',
    canAccessPrioritySupport: 'Suporte Prioritário',
    canUsePayeesModule: 'Módulo de Favorecidos',
  };

  const defaultDescriptions: Record<PermissionKey, string> = {
    canAccessReports: 'Acesse análises completas das suas finanças, gráficos de evolução, comparativos mensais e muito mais.',
    canExportCsv: 'Exporte todos os seus lançamentos e relatórios para planilhas CSV e analise no Excel ou Google Sheets.',
    canExportPdf: 'Gere relatórios financeiros formatados em PDF para compartilhar ou arquivar.',
    canAccessAdvancedInsights: 'Receba análises automáticas com IA sobre seus padrões de gastos e oportunidades de economia.',
    canUseMultipleCurrencies: 'Gerencie contas e lançamentos em diferentes moedas, com conversão automática.',
    canAccessLoansModule: 'Controle empréstimos, financiamentos e parcelas com cálculo de juros e amortização.',
    canAccessRecurringAdvanced: 'Configure recorrências com dia de vencimento, duração, parcelamento e financiamento.',
    canAccessPrioritySupport: 'Atendimento prioritário com tempo de resposta garantido por contrato.',
    canUsePayeesModule: 'Cadastre favorecidos, acompanhe gastos por pessoa/empresa, receba alertas inteligentes e use o auto-preenchimento no lançamento.',
  };

  const resolvedTitle = title ?? defaultTitles[feature];
  const resolvedDescription = description ?? defaultDescriptions[feature];

  // Track the block event for conversion analytics
  const handleUpgrade = () => {
    trackEvent('upgrade_click', { trigger: 'plan_gate', feature, mode });
    setShowUpgrade(true);
  };

  return (
    <>
      {mode === 'page' ? (
        <PageLocked
          feature={feature}
          title={resolvedTitle}
          description={resolvedDescription}
          planName={permissions.planName}
          onUpgrade={handleUpgrade}
        />
      ) : (
        <InlineLocked label={label} onUpgrade={handleUpgrade} />
      )}

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title={`Desbloquear: ${resolvedTitle}`}
        description={resolvedDescription}
        trigger="feature_block"
        feature={feature}
      />
    </>
  );
};
